import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MessageCircle, ShieldCheck, Crown, Clock, MapPin } from "lucide-react";
import { MaskLine } from "./motion";
import { BRAND, IMAGES } from "../../data";

const badges = [
  { icon: ShieldCheck, label: "Sécurité" },
  { icon: Crown, label: "Confort" },
  { icon: Clock, label: "Disponible 7j/7" },
  { icon: MapPin, label: "Toutes destinations" },
];

export const Hero = ({ onReserve }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden flex items-end">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Véhicule VTC de prestige la nuit" className="w-full h-[115%] object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-0 bg-[#060B14]" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#060B14] via-[#060B14]/30 to-transparent" />
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#060B14]/85 via-[#060B14]/30 to-transparent" />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-12 pb-20 lg:pb-28 pt-32">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="text-xs uppercase tracking-[0.35em] text-champagne font-semibold mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-champagne/60" /> {BRAND.subtitle} · Aube · Marne · Haute-Marne
        </motion.p>

        <h1 className="font-serif font-light tracking-tight text-[#F7F5F0] text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98] max-w-5xl">
          <MaskLine delay={0.35}>L'excellence</MaskLine>
          <MaskLine delay={0.5} className="italic text-champagne">à chaque trajet.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-[#94A3B8] text-base sm:text-lg leading-relaxed"
        >
          Chauffeur privé haut de gamme pour vos transferts aéroports, gares, déplacements professionnels et événements. Ponctuel, discret, disponible 7j/7.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button data-testid="hero-reserve-btn" onClick={onReserve} className="group inline-flex items-center gap-2 rounded-full bg-champagne text-[#060B14] px-8 py-4 font-semibold hover:opacity-90 transition-opacity">
            <MessageCircle size={18} /> Réserver votre course
          </button>
          <a data-testid="hero-call-link" href={`tel:+${BRAND.whatsapp}`} className="inline-flex items-center gap-2 rounded-full border border-white/20 text-[#F7F5F0] px-8 py-4 hover:bg-white hover:text-[#060B14] transition-colors">
            {BRAND.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/10 pt-8 max-w-2xl"
        >
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.label} className="flex items-center gap-2.5 text-[#94A3B8]">
                <Icon size={17} className="text-champagne" />
                <span className="text-sm tracking-wide">{b.label}</span>
              </div>
            );
          })}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 flex items-center gap-2 text-[#94A3B8] text-xs uppercase tracking-[0.2em]"
      >
        <ArrowDown size={14} className="animate-bounce" /> Défiler
      </motion.div>
    </section>
  );
};
